/**
 * Tiny REST client.
 *
 * Auth is an httpOnly session cookie set by /api/auth/login + /signup.
 * The cookie is JS-invisible (httpOnly), so this client never reads or
 * attaches a token — it just sends `credentials: 'include'` on every
 * request and the browser rides the cookie along. The backend bridges
 * the cookie to its auth dependencies (see the cookie→Bearer middleware
 * in backend/main.py). Cross-origin works because CORS allows the exact
 * frontend origin with credentials.
 */

import { API_BASE_URL, STORAGE_KEYS } from './constants';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export async function apiPost<T, B = unknown>(path: string, body: B, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    // FastAPI bodies are usually `{"detail": "..."}`. Try to surface
    // that as the error message so the UI can render it directly.
    let message = res.statusText;
    try {
      const j = await res.json();
      if (j && typeof j.detail === 'string') message = j.detail;
    } catch {
      try { message = (await res.text()) || res.statusText; } catch { /* fallthrough */ }
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<T>;
}

export async function apiPatch<T, B = unknown>(path: string, body: B, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PATCH',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    // Surface FastAPI's {"detail": "..."} so the admin UI can show the
    // backend's guardrail message (e.g. "can't demote yourself") inline.
    let message = res.statusText;
    try {
      const j = await res.json();
      if (j && typeof j.detail === 'string') message = j.detail;
    } catch {
      try { message = (await res.text()) || res.statusText; } catch { /* fallthrough */ }
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<T>;
}

/** Used by the legacy login form to validate creds before storing them
 *  as the basic-auth token. New sign-ins go through `apiPost`-backed
 *  `/api/auth/login` instead and never call this. Kept around so the
 *  existing Login.tsx error-handling code path doesn't break during
 *  the Day 3 migration. */
export async function apiVerifyAuth(username: string, password: string): Promise<boolean> {
  const token = btoa(`${username}:${password}`);
  const res = await fetch(`${API_BASE_URL}/me`, {
    headers: { Accept: 'application/json', Authorization: `Basic ${token}` },
  });
  return res.ok;
}

export function encodeCreds(username: string, password: string): string {
  return btoa(`${username}:${password}`);
}

/** Drop both auth storage keys (JWT + legacy basic). Use from logout
 *  flows so cross-tab `storage` listeners see a clean removal. */
export function clearStoredAuth(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.jwt);
    sessionStorage.removeItem(STORAGE_KEYS.creds);
  } catch {
    /* sessionStorage unavailable */
  }
}
